/**
 * `useDefaultMatchSuggestions` — lazy Haiku suggestion hook (DNE-3).
 *
 * market-default-apply-ux Phase 2 Session 2.5.
 *
 * Fetches the Phase-1 ownership-gated endpoint on flow-open (when `field` is
 * non-null). Fail-open contract: ANY error → empty suggestions, no error UI,
 * no toast. Callers render dropdown-only when suggestions is empty.
 *
 * Query key does NOT live in `query-keys.ts` registry — precedent:
 * `useApplyValue`'s ad-hoc `['market-data-cache', category, metro]` key
 * (research § 3 W-10).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ApplyMarketDefaultsResult } from '@arcanum-hq/shared-types';

/**
 * Derive `MarketFieldValueUnit` from the barrel-exported `ApplyMarketDefaultsResult`
 * (ADR 0007 — derive, not barrel-export `marketFieldValueUnitSchema`).
 * Shape: `{ marketField: string; value: number; unit?: string }`
 */
export type MarketFieldValueUnit =
  ApplyMarketDefaultsResult['informationalSet'][number];

export function useDefaultMatchSuggestions(args: {
  conversationId: string;
  /** null until a flow opens — hook is lazy (fires only when non-null). */
  field: MarketFieldValueUnit | null;
  eligibleInputs: Array<{
    name: string;
    displayName: string;
    dataType: string;
    description?: string;
  }>;
}) {
  const enabled = args.field !== null;

  const query = useQuery({
    queryKey: [
      'default-match-suggestions',
      args.conversationId,
      args.field?.marketField,
    ],
    enabled,
    retry: 0, // fail-open: do not retry a generation failure
    queryFn: async () => {
      try {
        const { suggestions } = await api.defaultMatchSuggestions(
          args.conversationId,
          {
            marketField: args.field!.marketField,
            value: args.field!.value,
            unit: args.field!.unit,
            eligibleInputs: args.eligibleInputs,
          }
        );
        return suggestions;
      } catch {
        // fail-open: ANY error → empty (dropdown-only, no error UI)
        return [];
      }
    },
  });

  return {
    suggestions: query.data ?? [],
    isLoading: query.isLoading && enabled,
  };
}
